/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { Box, Typography, Skeleton, Button } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
} from '@mui/icons-material';

// Add local dependencies.
import { MemberListProps } from '../../../types/studioMembers';
import MemberCard from './MemberCard';

const MemberList = ({
  members,
  loading = false,
  onRoleChange,
  onRemoveMember,
  onViewProfile,
  onSendMessage,
}: MemberListProps) => {
  // Loading skeleton
  if (loading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton
            key={`member-skeleton-${index}`}
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  // Empty state
  if (!members || members.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          textAlign: 'center',
        }}
      >
        <PeopleIcon
          sx={{
            fontSize: 64,
            color: 'text.secondary',
            mb: 2,
          }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No members found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This studio doesn&apos;t have any members yet, or your search
          didn&apos;t match any results.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          sx={{ mt: 1 }}
        >
          Invite Members
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {members.map(member => (
        <MemberCard
          key={member.id}
          member={member}
          onRoleChange={onRoleChange}
          onRemoveMember={onRemoveMember}
          onViewProfile={onViewProfile}
          onSendMessage={onSendMessage}
        />
      ))}
    </Box>
  );
};

export default MemberList;
